import PropTypes from "prop-types"
import React, { useRef } from "react"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Label,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import classNames from "classnames"

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"

//i18n
import { useTranslation, withTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { useState } from "react"
import { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Switch from "components/Shared/Switch"
import ReactApexChart from "react-apexcharts"
import {
  getDashboardKpi,
  getDashboardKpiOrder,
} from "store/actions/dashboardAction"
import { Bar, Pie } from "react-chartjs-2"
import "chart.js/auto"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import TableShimmer from "components/Shared/TableShimmer"
import { DateRangePicker, DatePicker } from "rsuite"
import "rsuite/dist/rsuite.min.css"
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  format,
} from "date-fns"
import { getAllCityList } from "store/actions/cityAction"
import { getAllRegionList } from "store/actions/regionAction"
import Select from "react-select"
import { allOption, timesUTC } from "components/Common/constants"
import Bookings from "./Bookings"
import isPermissionEnabled from "common/isPermissionEnabled"
import BranchRatings from "./BranchRatings"
import CommonDateFilter from "./CommonDateFilter"
import { getBranchCountryList } from "store/actions/commonAction"
import GiftCard from "./GiftCard"
import Wallet from "./Wallet"

const Dashboard = props => {
  const {
    i18n: { language },
  } = useTranslation()

  document.title = props.t("Dashboard | Namq")

  const urlParams = new URLSearchParams(window.location.search)
  const tabValue = urlParams.get("tab") || "sales"

  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 0 }) // Monday as start of the week
  const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 0 })

  const previousWeekStartDate = subDays(startOfCurrentWeek, 7)
  const previousWeekEndDate = subDays(endOfCurrentWeek, 7)

  const startOfMonthDate = startOfMonth(today)
  const endOfMonthDate = endOfMonth(today)

  const previousMonthStartDate = startOfMonth(subMonths(today, 1))
  const previousMonthEndDate = endOfMonth(subMonths(today, 1))

  const [tabs, setTabs] = useState(tabValue)
  const [periodType, setPeriodType] = useState("month")
  const [dayType, setDayType] = useState("month")
  const [commonDayType, setCommonDayType] = useState("month")
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(true)

  const [modalOpen, setModalOpen] = useState(false)
  const [country, setCountry] = useState([])
  const [region, setRegion] = useState([])
  const [city, setCity] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedBranches, setSelectedBranches] = useState([])
  const [selectedCountry, setSelectedCountry] = useState([])
  const [selectedRegion, setSelectedRegion] = useState([])
  const [selectedCity, setSelectedCity] = useState([])
  const [bookingModal, setBookingModal] = useState(false)
  const [ratingModal, setRatingModal] = useState(false)
  const [bookingType, setBookingType] = useState([])
  const [ratingBranches, setRatingBranches] = useState([])
  const [giftCardId, setGiftCardId] = useState([])
  const childFunctionRef = useRef(null)
  const ratingFunRef = useRef(null)

  const toggleModal = () => {
    setModalOpen(!modalOpen)
  }

  const { branchsList, countryList } = useSelector(state => state.Common)
  const { regionList } = useSelector(state => state.Region)
  const { cityList } = useSelector(state => state.City)
  const { giftCardList } = useSelector(state => state.GiftCard)

  const getDashboardData = () => {
    const countryValue = country.value ? [country.value] : []
    const regionValue = region.value ? [region.value] : []
    const cityValue = city.value ? [city.value] : []

    setIsLoading(true)
    const payload = {
      type: periodType, //day , month, week
      isCompared: periodType === "custom" ? false : isCompared,
      branches,
      date,
      comparisonDate,
      dateRange: periodType === "custom" ? customDateRange : dateRange,
      comparisonDateRange:
        periodType === "custom"
          ? comparisonCustomDateRange
          : comparisonDateRange,
      cities: cityValue,
      regions: regionValue,
      countries: countryValue,
    }
    dispatch(getDashboardKpi(payload))
  }

  const getDashboardOrderData = () => {
    const countryValue = country.value ? [country.value] : []
    const regionValue = region.value ? [region.value] : []
    const cityValue = city.value ? [region.value] : []

    const payload = {
      dateRange: commonDateRange,
      branches,
      cities: cityValue,
      regions: regionValue,
      countries: countryValue,
    }
    dispatch(getDashboardKpiOrder(payload))
  }

  useEffect(() => {
    fetchBranchCountryList()
  }, [])

  const fetchBranchCountryList = () => {
    dispatch(getBranchCountryList())
  }

  const fetchRegionList = countryId => {
    dispatch(getAllRegionList(countryId))
  }

  const fetchCityList = (countryId, regionId) => {
    const payload = {
      countryId,
      regionId,
    }
    dispatch(getAllCityList(payload))
  }

  const handleTabClick = tab => {
    setIsAllSelected(false)
    setSelectedBranches([])
    setBranches([])
    setCountry([])
    setRegion([])
    setCity([])
    setSelectedCountry([])
    setSelectedRegion([])
    setSelectedCity([])
    setTabs(tab)
    navigate(`/dashboard?tab=${tab}`)
  }

  const convertTimeToUTCDate = timeString => {
    const [time, period] = timeString.split(" ")
    let [hours, minutes] = time.split(":")

    // Default minutes to 0 if not provided
    minutes = minutes || "00"

    // Handle AM/PM conversion
    hours = parseInt(hours, 10)
    if (period === "PM" && hours !== 12) {
      hours += 12
    } else if (period === "AM" && hours === 12) {
      hours = 0
    }
    // Create a new Date object with the UTC time
    const date = new Date(Date.UTC(2024, 0, 1, hours, minutes, 0))
    return date
  }

  const convertToLocaleTime = utcDate => {
    return utcDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  }

  const convertTimesArrayToLocale = timesArray => {
    return timesArray.map(time =>
      convertToLocaleTime(convertTimeToUTCDate(time))
    )
  }

  // CONVERT DATE TO LOCAL DATE
  const convertDateToUTC = dateString => {
    const [year, month, day] = dateString.split("-").map(Number)
    // Create a new Date object with the UTC date
    const date = new Date(Date.UTC(year, month - 1, day))
    return date
  }

  const convertToLocaleDate = utcDate => {
    // Get the date in local timezone and format it as YYYY-MM-DD
    const localDate = new Date(
      utcDate.getTime() - utcDate.getTimezoneOffset() * 60000
    )
    return localDate.toISOString().split("T")[0]
  }

  const convertDatesArrayToLocale = datesArray => {
    return datesArray.map(date => convertToLocaleDate(convertDateToUTC(date)))
  }

  const formatDate = date => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const day = date.getDate().toString().padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const formattedToday = formatDate(today)
  // Format yesterday's date as "YYYY-MM-DD"
  const formattedYesterday = formatDate(yesterday)

  // DEFAULT WEEK DAY
  const formattedStartOfCurrentWeek = formatDate(startOfCurrentWeek)
  const formattedEndOfCurrentWeek = formatDate(endOfCurrentWeek)
  const formattedPreviousWeekStartDate = formatDate(previousWeekStartDate)
  const formattedPreviousWeekEndDate = formatDate(previousWeekEndDate)

  const formattedStartOfMonth = format(startOfMonthDate, "yyyy-MM-dd")
  const formattedEndOfMonth = format(endOfMonthDate, "yyyy-MM-dd")
  const formattedPreviousMonthStartDate = format(
    previousMonthStartDate,
    "yyyy-MM-dd"
  )
  const formattedPreviousMonthEndDate = format(
    previousMonthEndDate,
    "yyyy-MM-dd"
  )

  const [isCompared, setIsCompared] = useState(false)
  const [date, setDate] = useState(formattedToday)
  const [dateRange, setDateRange] = useState({
    start: formattedStartOfMonth,
    end: formattedEndOfMonth,
  })

  const [commonDateRange, setCommonDateRange] = useState({
    start: formattedStartOfMonth,
    end: formattedEndOfMonth,
  })

  const [comparisonDateRange, setComparisonDateRange] = useState({
    start: formattedPreviousWeekStartDate,
    end: formattedPreviousWeekEndDate,
  })
  const [customDateRange, setCustomDateRange] = useState({
    start: formattedYesterday,
    end: formattedToday,
  })
  const [comparisonCustomDateRange, setComparisonCustomDateRange] = useState({
    start: formattedYesterday,
    end: formattedToday,
  })

  useEffect(() => {
    if (isCompared) {
      if (dayType === "week") {
        setComparisonDateRange({
          start: formattedPreviousWeekStartDate,
          end: formattedPreviousWeekEndDate,
        })
      } else if (dayType === "month") {
        setComparisonDateRange({
          start: formattedPreviousMonthStartDate,
          end: formattedPreviousMonthEndDate,
        })
      }
    }
  }, [isCompared, dayType])

  const [comparisonDate, setComparisonDate] = useState(formattedYesterday)

  useEffect(() => {
    // console.log("2")
    // if (tabs !== "Eidiyah") {
      tabs === "sales" && !isCompared && getDashboardData()
    // }
  }, [
    // isCompared,
    comparisonDate,
    periodType,
    tabs,
    date,
    dateRange,
    branches,
    // customDateRange,
    // comparisonDateRange,
  ])

  useEffect(() => {
    tabs === "branches" && getDashboardOrderData()
  }, [tabs, commonDateRange, country, region, city, branches])

  useEffect(() => {
    // console.log("1")
    // if (tabs !== "Eidiyah") {
      tabs === "sales" && getDashboardData()
    // }
  }, [
    // periodType,
    date,
    isCompared,
    comparisonDateRange,
    branches,
    comparisonDate,
    customDateRange,
    comparisonCustomDateRange,
  ])

  useEffect(() => {
    if (isCompared && periodType === "day" && tabs !== "Eidiyah") {
      getDashboardData()
    }
  }, [isCompared, periodType])

  const { userRole, accountType } = useSelector(state => state.Login)
  const { loading, dashboardData, dashboardOrderData } = useSelector(
    state => state.Dashboard
  )

  const [usersPermissions, setUsersPermissions] = useState({
    isEidiyah: null,
    isSales: null,
    isBranches: null,
    isBookings: null,
    isRatings: null,
    isWallet: null,
  })

  useEffect(() => {
    const isEidiyahPermission = isPermissionEnabled(
      userRole,
      1,
      "dashboard_5",
      accountType
    )
    const isSalesPermission = isPermissionEnabled(
      userRole,
      1,
      "dashboard_1",
      accountType
    )
    const isBranchesPermission = isPermissionEnabled(
      userRole,
      1,
      "dashboard_2",
      accountType
    )
    const isBookingsPermission = isPermissionEnabled(
      userRole,
      1,
      "dashboard_3",
      accountType
    )
    const isRatingsPermission = isPermissionEnabled(
      userRole,
      1,
      "dashboard_4",
      accountType
    )
    const isWalletPermission = isPermissionEnabled(
      userRole,
      1,
      "dashboard_6",
      accountType
    )
    setUsersPermissions({
      isEidiyah: isEidiyahPermission,
      isSales: isSalesPermission,
      isBranches: isBranchesPermission,
      isBookings: isBookingsPermission,
      isRatings: isRatingsPermission,
      isWallet: isWalletPermission,
    })

    // Set tabs based on permissions
    if (isEidiyahPermission) {
      // setTabs("Eidiyah")
    } else if (isSalesPermission) {
      setTabs("sales")
    } else if (isBranchesPermission) {
      setTabs("branches")
    } else if (isBookingsPermission) {
      setTabs("bookings")
    } else if (isRatingsPermission) {
      setTabs("ratings")
    } else if (isWalletPermission) {
      setTabs("wallet")
    }
  }, [])

  useEffect(() => {
    checkPermissions(userRole?.permissions, 1, accountType)
  }, [])

  const handleBranchFilter = () => {
    const branchIds = selectedBranches?.map(branch => branch.value)
    setBranches(branchIds)
    setCountry(selectedCountry)
    setRegion(selectedRegion)
    setCity(selectedCity)
    toggleModal()
  }

  const routesMapping = {
    inAppContent_1: "/banners",
    roles_1: "/roles",
    menus_1: "/menus/categories",
    branchLocations_1: "/branches",
    usersPermission_1: "/users",
    customersPermission_1: "/customers",
    feedback_1: "#",
    order_1: "/orders",
    curbside_1: "#",
    country_1: "/countries",
  }

  const checkPermissions = (permissions, idToCheck) => {
    const firstEnabledPermission = permissions?.reduce((acc, role) => {
      if (!acc) {
        const enabledPermission = role.permissions.find(
          p => p.value === "view" && p.isEnabled
        )
        if (enabledPermission) {
          acc = enabledPermission
        }
      }
      return acc
    }, null)

    // console.log(firstEnabledPermission)
    if (firstEnabledPermission) {
      let p = firstEnabledPermission
      const route = routesMapping[p.id]
      if (route) {
        navigate(route)
      } else {
        console.log("url not found")
      }
    }
  }

  const onChangeChartPeriod = (pType, type) => {
    setPeriodType(pType)
    setDayType(type)
    if (type === "day") {
      setDate(formattedToday)
    } else if (type === "week") {
      setDateRange({
        start: formattedStartOfCurrentWeek,
        end: formattedEndOfCurrentWeek,
      })
    } else if (type === "month") {
      setDateRange({
        start: formattedStartOfMonth,
        end: formattedEndOfMonth,
      })
    } else if (type === "custom") {
      setIsCompared(false)
      setCustomDateRange({
        start: formattedYesterday,
        end: formattedToday,
      })
    }
  }

  const handleOnDateChange = (selectedDates, type) => {
    const formattedDate = `${selectedDates.getFullYear()}-${(
      selectedDates.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${selectedDates.getDate().toString().padStart(2, "0")}`

    if (type === "day") {
      setDate(formattedDate)
    } else if (type === "comparisonDate") {
      setComparisonDate(formattedDate)
    }
  }

  const handleDateRangeChange = value => {
    setDateRange({
      start: formatDate(value[0]),
      end: formatDate(value[1]),
    })
  }

  const handleCustomDateRangeChange = value => {
    setCustomDateRange({
      start: formatDate(value[0]),
      end: formatDate(value[1]),
    })
  }

  const handleComparisonDateRangeChange = value => {
    setComparisonDateRange({
      start: formatDate(value[0]),
      end: formatDate(value[1]),
    })
  }

  const [options, setOptions] = useState({
    chart: {
      id: "line-chart",
      toolbar: {
        show: false,
      },
    },
    stroke: {
      width: 2,
      curve: "smooth",
    },
    xaxis: {
      categories: [],
      type: "category",
    },
  })

  const [orderBranchData, setOrderBranchData] = useState({
    labels: [],
    datasets: [
      {
        label: "",
        data: [],
      },
    ],
  })

  const [comparisonOrderBranchData, setComparisonOrderBranchData] = useState({
    labels: [],
    datasets: [
      {
        label: "",
        data: [],
      },
    ],
  })

  const [mostOrderedItemsData, setMostOrderedItemsData] = useState({
    labels: [],
    datasets: [
      {
        label: "",
        data: [],
      },
    ],
  })
  const [comparisonMostOrderedItemsData, setComparisonMostOrderedItemsData] =
    useState({
      labels: [],
      datasets: [
        {
          label: "",
          data: [],
        },
      ],
    })

  const [mostPaymentMethodData, setMostPaymentMethodData] = useState({
    labels: [],
    datasets: [
      {
        label: "",
        data: [],
      },
    ],
  })
  const [comparisonMostPaymentMethodData, setComparisonMostPaymentMethodData] =
    useState({
      labels: [],
      datasets: [
        {
          label: "",
          data: [],
        },
      ],
    })

  const [orderSeries, setOrderSeries] = useState([])
  const [totalOrders, setTotalOrders] = useState(null)

  const [netSalesSeries, setNetSalesSeries] = useState([])
  const [totalSales, setTotalSales] = useState(null)

  const [netPaymentSeries, setNetPaymentSeries] = useState([])
  const [totalPayment, setTotalPayment] = useState(null)

  const [discountSeries, setDiscountSeries] = useState([])
  const [totalDiscount, setTotalDiscount] = useState(null)

  const [orderTypeSeries, setOrderTypeSeries] = useState([])

  const [hourlySalesData, setHourlySalesData] = useState({})
  const [barOptions, setBarOptions] = useState({
    scales: {
      x: {
        barPercentage: 0.4,
      },
      y: {},
    },
  })

  function mapSeriesData(series) {
    return series?.map(item => ({
      name: item?.name,
      data: item?.data?.map(value =>
        typeof value === "number" ? Number(value?.toFixed(2)) : value
      ),
    }))
  }

  // Function to calculate sum of data
  function calculateSum(series) {
    let sum = 0
    series.forEach(item => {
      sum += item.data.reduce((acc, curr) => acc + curr, 0)
    })
    return sum
  }

  const backgroundColor = [
    "#008ffb",
    "#00e396",
    "#feb019",
    "#ff4560",
    "#775dd0",
    "#008ffb",
    "#00e396",
    "#feb019",
    "#ff4560",
    "#775dd0",
  ]

  const mergeArrays = (arr1, arr2) => {
    return [...arr1, ...arr2]
  }

  useEffect(() => {
    // Check if dashboardData exists and has the necessary structure
    if (
      dashboardData &&
      dashboardData.mainData &&
      dashboardData.mainData.orders &&
      dashboardData.mainData.orders.series
    ) {
      const categories = dashboardData?.mainData?.categories
      const timesLocalCategories =
        periodType === "day"
          ? convertTimesArrayToLocale(categories)
          : convertDatesArrayToLocale(categories)

      setOptions(prevOptions => ({
        ...prevOptions,
        xaxis: {
          ...prevOptions.xaxis,
          categories: timesLocalCategories,
          type: "category",
        },
      }))

      //________________GET ORDER SERIES________________

      const mainOrderSeries = mapSeriesData(
        dashboardData?.mainData?.orders?.series
      )
      const comparisonOrderSeries = mapSeriesData(
        dashboardData?.comparisonData?.orders?.series
      )

      const updatedOrderSeries = isCompared
        ? mergeArrays(mainOrderSeries, comparisonOrderSeries)
        : mainOrderSeries

      setOrderSeries(updatedOrderSeries)

      // ________________GET SALES SERIES________________
      const mainSalesSeries = mapSeriesData(
        dashboardData?.mainData?.sales?.series
      )
      const comparisonSalesSeries = mapSeriesData(
        dashboardData?.comparisonData?.sales?.series
      )

      const updatedSalesSeries = isCompared
        ? mergeArrays(mainSalesSeries, comparisonSalesSeries)
        : mainSalesSeries

      setNetSalesSeries(updatedSalesSeries)

      // ________________GET NET REVENUE SERIES________________
      const mainPaymentSeries = mapSeriesData(
        dashboardData?.mainData?.revenue?.series
      )
      const comparisonPaymentSeries = mapSeriesData(
        dashboardData?.comparisonData?.revenue?.series
      )

      const updatedPaymentSeries = isCompared
        ? mergeArrays(mainPaymentSeries, comparisonPaymentSeries)
        : mainPaymentSeries

      setNetPaymentSeries(updatedPaymentSeries)

      // ________________GET DISCOUNT SERIES________________
      const mainDiscountSeries = mapSeriesData(
        dashboardData?.mainData?.discount?.series
      )
      const comparisonDiscountSeries = mapSeriesData(
        dashboardData?.comparisonData?.discount?.series
      )
      const updatedDiscountSeries = isCompared
        ? mergeArrays(mainDiscountSeries, comparisonDiscountSeries)
        : mainDiscountSeries

      setDiscountSeries(updatedDiscountSeries)

      // ________________GET ORDER TYPE SERIES________________
      const mainOrderTypeSeries = mapSeriesData(
        dashboardData?.mainData?.orderTypes?.series
      )
      const comparisonOrderTypeSeries = mapSeriesData(
        dashboardData?.comparisonData?.orderTypes?.series
      )

      const updatedOrderTypeSeries = isCompared
        ? mergeArrays(mainOrderTypeSeries, comparisonOrderTypeSeries)
        : mainOrderTypeSeries

      setOrderTypeSeries(updatedOrderTypeSeries)

      // ________________CALCULATE TOTAL ORDERS________________
      const orderSum = calculateSum(dashboardData?.mainData?.orders?.series)
      setTotalOrders(orderSum)

      // ________________CALCULATE TOTAL SALES________________
      const salesSum = calculateSum(dashboardData?.mainData?.sales?.series)
      setTotalSales(salesSum)

      // ________________CALCULATE TOTAL NET PAYMENT________________
      const paymentSum = calculateSum(dashboardData?.mainData?.revenue?.series)
      setTotalPayment(paymentSum)

      // ________________CALCULATE TOTAL DISCOUNT________________
      const discountSum = calculateSum(
        dashboardData?.mainData?.discount?.series
      )
      setTotalDiscount(discountSum)

      // ________________MOST ORDERS BRANCH________________

      const mostOrderedOrderLabels =
        dashboardData?.mainData?.pieChartData?.mostOrderedBranch?.map(item =>
          language === "sa" ? item.nameAr : item.name
        )

      const mostOrderedBranchSeries =
        dashboardData?.mainData?.pieChartData?.mostOrderedBranch?.map(
          item => item.amount
        )

      const mostOrderedBranchCounts =
        dashboardData?.mainData?.pieChartData?.mostOrderedBranch?.map(
          item => item.count
        )

      setOrderBranchData(prevOptions => ({
        ...prevOptions,
        labels: mostOrderedOrderLabels,
        datasets: [
          {
            label: "",
            data: mostOrderedBranchSeries,
            backgroundColor: backgroundColor,
            counts: mostOrderedBranchCounts,
          },
        ],
      }))

      const mostComparisonOrderedOrderLabels =
        dashboardData?.comparisonData?.pieChartData?.mostOrderedBranch?.map(
          item => (language === "sa" ? item.nameAr : item.name)
        )

      const mostComparisonOrderedBranchSeries =
        dashboardData?.comparisonData?.pieChartData?.mostOrderedBranch?.map(
          item => item.amount
        )
      setComparisonOrderBranchData(prevOptions => ({
        ...prevOptions,
        labels: mostComparisonOrderedOrderLabels,
        datasets: [
          {
            label: "",
            data: mostComparisonOrderedBranchSeries,
            backgroundColor: backgroundColor,
          },
        ],
      }))

      // ________________MOST ORDER ITEM________________
      const mostOrderedItemsLabels =
        dashboardData?.mainData?.pieChartData?.mostOrderedItems?.map(item =>
          language === "sa" ? item.nameAr : item.name
        )
      const mostOrderedItemSeries =
        dashboardData?.mainData?.pieChartData?.mostOrderedItems?.map(
          item => item.count
        )
      setMostOrderedItemsData(prevOptions => ({
        ...prevOptions,
        labels: mostOrderedItemsLabels,
        datasets: [
          {
            label: "",
            data: mostOrderedItemSeries,
            backgroundColor: backgroundColor,
          },
        ],
      }))
      const mostComparisonOrderedItemsLabels =
        dashboardData?.comparisonData?.pieChartData?.mostOrderedItems?.map(
          item => (language === "sa" ? item.nameAr : item.name)
        )
      const mostComparisonOrderedItemSeries =
        dashboardData?.comparisonData?.pieChartData?.mostOrderedItems?.map(
          item => item.count
        )
      setComparisonMostOrderedItemsData(prevOptions => ({
        ...prevOptions,
        labels: mostComparisonOrderedItemsLabels,
        datasets: [
          {
            label: "",
            data: mostComparisonOrderedItemSeries,
            backgroundColor: backgroundColor,
          },
        ],
      }))

      // ________________MOST PAYMENT METHOD________________
      const mostPaymentMethodLabels =
        dashboardData?.mainData?.pieChartData?.mostUsedPaymentMethod?.map(
          item => props.t(item._id)
        )
      const mostPaymentMethodSeries =
        dashboardData?.mainData?.pieChartData?.mostUsedPaymentMethod?.map(
          item => item.count
        )
      setMostPaymentMethodData(prevOptions => ({
        ...prevOptions,
        labels: mostPaymentMethodLabels,
        datasets: [
          {
            label: "",
            data: mostPaymentMethodSeries,
            backgroundColor: backgroundColor,
          },
        ],
      }))
      const mostComparisonPaymentMethodLabels =
        dashboardData?.comparisonData?.pieChartData?.mostUsedPaymentMethod?.map(
          item => props.t(item._id)
        )
      const mostComparisonPaymentMethodSeries =
        dashboardData?.comparisonData?.pieChartData?.mostUsedPaymentMethod?.map(
          item => item.count
        )
      setComparisonMostPaymentMethodData(prevOptions => ({
        ...prevOptions,
        labels: mostComparisonPaymentMethodLabels,
        datasets: [
          {
            label: "",
            data: mostComparisonPaymentMethodSeries,
            backgroundColor: backgroundColor,
          },
        ],
      }))

      // ________________HOURLY SALES________________
      const mainHourlySalesLabel =
        dashboardData?.mainData?.hourlySales?.series[0]?.name
      const MainHourlySalesSeries =
        dashboardData?.mainData?.hourlySales?.series[0]?.data

      const comparisonHourlySalesLabel =
        dashboardData?.comparisonData?.hourlySales?.series[0]?.name
      const comparisonMainHourlySalesSeries =
        dashboardData?.comparisonData?.hourlySales?.series[0]?.data

      const mainHourlySalesData = {
        label: mainHourlySalesLabel,
        borderWidth: 1,
        data: MainHourlySalesSeries,
      }
      const comparisonHourlySalesData = {
        label: comparisonHourlySalesLabel,
        borderWidth: 1,
        data: comparisonMainHourlySalesSeries,
      }

      const hourlyDataSets = isCompared
        ? [mainHourlySalesData, comparisonHourlySalesData]
        : [mainHourlySalesData]

      const timesLocal = convertTimesArrayToLocale(timesUTC)

      setHourlySalesData({
        labels: timesLocal,
        datasets: hourlyDataSets,
      })

      setTimeout(() => {
        setIsLoading(false)
      }, 1500)
    }
  }, [dashboardData, language])

  const [isAllSelected, setIsAllSelected] = useState(false)

  const branchListOptions = branchsList?.map(item => ({
    label: language === "sa" ? item.nameAr : item.name,
    value: item._id,
  }))

  const eidiyahListOptions = giftCardList?.map(item => ({
    label: language === "sa" ? item.nameAr : item.name,
    value: item._id,
  }))

  const branchOptions = isAllSelected
    ? branchListOptions
    : [allOption, ...branchListOptions]

  const handleBookingReset = () => {
    if (childFunctionRef.current) {
      childFunctionRef.current()
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumbs title={props.t("Dashboards")} />

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle className="fs-4">
                    {props.t("Welcome to Namq!")}
                  </CardTitle>
                  <Col>
                    <div className="dashboard_top_tabs">
                      <div className="d-flex align-items-center justify-content-between">
                        <ul className="d-flex ps-0 mt-3 mb-2">
                          {/* {usersPermissions.isEidiyah && (
                            <li
                              className={`fs-5 ${
                                tabs === "Eidiyah" ? "active" : ""
                              }`}
                              onClick={() => handleTabClick("Eidiyah")}
                            >
                              {props.t("Eidiyah")}
                            </li>
                          )} */}
                          {usersPermissions.isSales && (
                            <li
                              className={`fs-5 ${
                                tabs === "sales" ? "active" : ""
                              }`}
                              onClick={() => handleTabClick("sales")}
                            >
                              {props.t("Sales")}
                            </li>
                          )}
                          {usersPermissions.isBranches && (
                            <li
                              className={`fs-5  ${
                                tabs === "branches" ? "active" : ""
                              }`}
                              onClick={() => handleTabClick("branches")}
                            >
                              {props.t("Branches")}
                            </li>
                          )}
                          {usersPermissions.isRatings && (
                            <li
                              className={`fs-5  ${
                                tabs === "ratings" ? "active" : ""
                              }`}
                              onClick={() => handleTabClick("ratings")}
                            >
                              {props.t("Ratings")}
                            </li>
                          )}
                          {usersPermissions.isBookings && (
                            <li
                              className={`fs-5 ${
                                tabs === "bookings" ? "active" : ""
                              }`}
                              onClick={() => handleTabClick("bookings")}
                            >
                              {props.t("Bookings")}
                            </li>
                          )}
                          {usersPermissions.isWallet && (
                            <li
                              className={`fs-5 ${
                                tabs === "wallet" ? "active" : ""
                              }`}
                              onClick={() => handleTabClick("wallet")}
                            >
                              {props.t("Wallet")}
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </Col>
                  {tabs === "sales" ? null : (
                    <Col className="mt-3">
                      <div className="d-flex align-items-start justify-content-between">
                        <CommonDateFilter
                          isLoading={isLoading}
                          onChangeChartPeriod={onChangeChartPeriod}
                          date={date}
                          commonDateRange={commonDateRange}
                          setCommonDateRange={setCommonDateRange}
                          formattedToday={formattedToday}
                          formattedStartOfCurrentWeek={
                            formattedStartOfCurrentWeek
                          }
                          formattedEndOfCurrentWeek={formattedEndOfCurrentWeek}
                          formattedStartOfMonth={formattedStartOfMonth}
                          formattedEndOfMonth={formattedEndOfMonth}
                          formattedYesterday={formattedYesterday}
                          formatDate={formatDate}
                          commonDayType={commonDayType}
                          setCommonDayType={setCommonDayType}
                          t={props.t}
                        />
                        {tabs === "ratings" && (
                          <div style={{ minWidth: 250 }}>
                            <Select
                              value={ratingBranches}
                              isMulti={true}
                              onChange={item => {
                                if (
                                  item?.find(option => option.value === "*")
                                ) {
                                  setRatingBranches(branchListOptions)
                                  setIsAllSelected(true)
                                } else {
                                  setRatingBranches(item)
                                  setIsAllSelected(
                                    item.length === branchListOptions.length
                                  )
                                }
                              }}
                              options={branchOptions}
                              placeholder={props.t("Select Branches")}
                              closeMenuOnSelect={false}
                            />
                          </div>
                        )}

                        {/* {tabs === "Eidiyah" && (
                          <div style={{ minWidth: 250 }}>
                            <Select
                              value={giftCardId}
                              isMulti={true}
                              onChange={item => {
                                if (
                                  item?.find(option => option.value === "*")
                                ) {
                                  setGiftCardId(eidiyahListOptions)
                                  setIsAllSelected(true)
                                } else {
                                  setGiftCardId(item)
                                  setIsAllSelected(
                                    item.length === eidiyahListOptions.length
                                  )
                                }
                              }}
                              options={eidiyahListOptions}
                              placeholder={props.t("Select Eidiyah Lists")}
                              closeMenuOnSelect={false}
                            />
                          </div>
                        )} */}

                        {tabs === "bookings" && (
                          <div className="d-flex align-items-center justify-content-between mt-3">
                            <button
                              className="g_center"
                              style={{
                                background: "transparent",
                                borderRadius: 8,
                                minWidth: 40,
                                height: 40,
                                border: "1px solid #646A53",
                              }}
                            >
                              <i
                                onClick={() => setBookingModal(!bookingModal)}
                                className="bx bxs-filter-alt fs-4"
                                style={{ color: "#646A53" }}
                              ></i>
                              {bookingType && bookingType.length > 0 && (
                                <i
                                  className="mdi mdi-close-circle-outline fs-4"
                                  style={{
                                    color: "rgb(148, 151, 167)",
                                    marginLeft: 5,
                                  }}
                                  onClick={() => {
                                    setBookingType([])
                                    handleBookingReset()
                                  }}
                                ></i>
                              )}
                            </button>
                          </div>
                        )}

                        {tabs === "branches" && (
                          <button
                            className="g_center"
                            style={{
                              background: "transparent",
                              borderRadius: 8,
                              minWidth: 40,
                              height: 40,
                              border: "1px solid #646A53",
                            }}
                          >
                            <i
                              onClick={toggleModal}
                              className="bx bxs-filter-alt fs-4"
                              style={{ color: "#646A53" }}
                            ></i>{" "}
                            {(selectedBranches.length > 0 ||
                              Object.keys(selectedCountry).length > 0 ||
                              Object.keys(selectedRegion).length > 0 ||
                              Object.keys(selectedCity).length > 0) && (
                              <i
                                className="mdi mdi-close-circle-outline fs-4"
                                style={{
                                  color: "rgb(148, 151, 167)",
                                  marginLeft: 5,
                                }}
                                onClick={() => {
                                  setSelectedBranches([])
                                  setSelectedCountry([])
                                  setSelectedRegion([])
                                  setSelectedCity([])
                                  setBranches([])
                                  setCountry([])
                                  setRegion([])
                                  setCity([])
                                }}
                              ></i>
                            )}
                          </button>
                        )}
                      </div>
                    </Col>
                  )}

                  {tabs === "sales" && (
                    <Col>
                      <div className="ms-auto mt-3 ">
                        <div className="d-flex align-items-center justify-content-between">
                          {/* <ul className="nav nav-pills" style={{ minWidth: 220 }}> */}
                          <div className="d-flex align-items-center">
                            <ul
                              className="nav nav-pills"
                              style={{ minWidth: 80 }}
                            >
                              <li className="nav-item">
                                <Link
                                  to="#"
                                  className={classNames(
                                    { active: dayType === "day" },
                                    { "disabled-link": isLoading },
                                    "nav-link"
                                  )}
                                  onClick={() => {
                                    !isLoading &&
                                      onChangeChartPeriod("day", "day")
                                  }}
                                  id="one_month"
                                >
                                  {props.t("Day")}
                                </Link>{" "}
                              </li>
                              <li className="nav-item">
                                <Link
                                  to="#"
                                  className={classNames(
                                    { active: dayType === "week" },
                                    { "disabled-link": isLoading },
                                    "nav-link"
                                  )}
                                  onClick={() => {
                                    !isLoading &&
                                      onChangeChartPeriod("week", "week")
                                  }}
                                >
                                  {props.t("Week")}
                                </Link>{" "}
                              </li>
                              <li className="nav-item">
                                <Link
                                  to="#"
                                  className={classNames(
                                    { active: dayType === "month" },
                                    { "disabled-link": isLoading },
                                    "nav-link"
                                  )}
                                  onClick={() => {
                                    !isLoading &&
                                      onChangeChartPeriod("month", "month")
                                  }}
                                >
                                  {props.t("Month")}
                                </Link>{" "}
                              </li>
                              <li className="nav-item">
                                <Link
                                  to="#"
                                  className={classNames(
                                    { active: dayType === "custom" },
                                    { "disabled-link": isLoading },
                                    "nav-link"
                                  )}
                                  onClick={() => {
                                    !isLoading &&
                                      onChangeChartPeriod("custom", "custom")
                                  }}
                                >
                                  {props.t("Custom")}
                                </Link>{" "}
                              </li>
                              {/* SHOW */}

                              <li style={{ maxWidth: 115, marginLeft: 5 }}>
                                {dayType === "day" && (
                                  <DatePicker
                                    format="yyyy-MM-dd"
                                    defaultValue={new Date(date)}
                                    onChange={value =>
                                      handleOnDateChange(value, "day")
                                    }
                                  />
                                )}
                              </li>

                              <li style={{ maxWidth: 210, marginLeft: 10 }}>
                                {dayType === "week" && (
                                  <DateRangePicker
                                    format="yyyy-MM-dd"
                                    defaultValue={[
                                      new Date(dateRange.start),
                                      new Date(dateRange.end),
                                    ]}
                                    hoverRange="week"
                                    ranges={[]}
                                    oneTap
                                    isoWeek
                                    onChange={handleDateRangeChange}
                                  />
                                )}
                              </li>
                              <li style={{ maxWidth: 210, marginLeft: 10 }}>
                                {dayType === "month" && (
                                  <DateRangePicker
                                    format="yyyy-MM-dd"
                                    defaultValue={[
                                      new Date(formattedStartOfMonth),
                                      new Date(formattedEndOfMonth),
                                    ]}
                                    hoverRange="month"
                                    ranges={[]}
                                    oneTap
                                    isoWeek
                                    onChange={handleDateRangeChange}
                                  />
                                )}
                              </li>

                              <li style={{ maxWidth: 200, marginLeft: 5 }}>
                                {dayType === "custom" && (
                                  <DateRangePicker
                                    format="yyyy-MM-dd"
                                    defaultValue={[
                                      new Date(customDateRange.start),
                                      new Date(customDateRange.end),
                                    ]}
                                    ranges={[]}
                                    onChange={handleCustomDateRangeChange}
                                  />
                                )}
                              </li>
                            </ul>
                            {dayType === "custom" ? null : (
                              <div className="d-flex align-items-center mx-1">
                                <h6 className="mx-2 mb-0">
                                  {props.t("Compare")}
                                </h6>
                                <Switch
                                  onClickHandler={() =>
                                    setIsCompared(!isCompared)
                                  }
                                  disabled={isLoading}
                                  isChecked={isCompared}
                                />
                              </div>
                            )}
                            {isCompared && dayType === "day" && (
                              <div style={{ width: 115, marginLeft: 10 }}>
                                <DatePicker
                                  format="yyyy-MM-dd"
                                  defaultValue={new Date(yesterday)}
                                  onChange={value =>
                                    handleOnDateChange(value, "comparisonDate")
                                  }
                                />
                              </div>
                            )}
                            {isCompared && dayType === "week" && (
                              <div style={{ maxWidth: 210, marginLeft: 10 }}>
                                <DateRangePicker
                                  format="yyyy-MM-dd"
                                  value={[
                                    new Date(comparisonDateRange.start),
                                    new Date(comparisonDateRange.end),
                                  ]}
                                  hoverRange="week"
                                  ranges={[]}
                                  oneTap
                                  isoWeek
                                  onChange={handleComparisonDateRangeChange}
                                />
                              </div>
                            )}
                            {isCompared && dayType === "month" && (
                              <div style={{ maxWidth: 210, marginLeft: 10 }}>
                                <DateRangePicker
                                  format="yyyy-MM-dd"
                                  value={[
                                    new Date(comparisonDateRange.start),
                                    new Date(comparisonDateRange.end),
                                  ]}
                                  hoverRange="month"
                                  ranges={[]}
                                  oneTap
                                  isoWeek
                                  onChange={handleComparisonDateRangeChange}
                                />
                              </div>
                            )}
                          </div>

                          <button
                            className="g_center"
                            style={{
                              background: "transparent",
                              borderRadius: 8,
                              minWidth: 40,
                              height: 40,
                              border: "1px solid #646A53",
                            }}
                          >
                            <i
                              onClick={toggleModal}
                              className="bx bxs-filter-alt fs-4"
                              style={{ color: "#646A53" }}
                            ></i>{" "}
                            {(selectedBranches.length > 0 ||
                              Object.keys(selectedCountry).length > 0 ||
                              Object.keys(selectedRegion).length > 0 ||
                              Object.keys(selectedCity).length > 0) && (
                              <i
                                className="mdi mdi-close-circle-outline fs-4"
                                style={{
                                  color: "rgb(148, 151, 167)",
                                  marginLeft: 5,
                                }}
                                onClick={() => {
                                  setSelectedBranches([])
                                  setSelectedCountry([])
                                  setSelectedRegion([])
                                  setSelectedCity([])
                                  setBranches([])
                                  setCountry([])
                                  setRegion([])
                                  setCity([])
                                }}
                              ></i>
                            )}
                          </button>
                        </div>
                      </div>
                    </Col>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {tabs === "Eidiyah" && (
            <GiftCard
              t={props.t}
              giftCardId={giftCardId}
              dateRange={dateRange}
              commonDayType={commonDayType}
              periodType={periodType}
              customDateRange={customDateRange}
              commonDateRange={commonDateRange}
            />
          )}

          {tabs === "sales" && (
            <>
              <Row>
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Total Orders")}</CardTitle>
                      {isLoading ? (
                        <ShimmerThumbnail height={320} rounded />
                      ) : (
                        <>
                          <h2>{totalOrders}</h2>
                          <div style={{ direction: "ltr" }}>
                            {orderSeries.length === 0 && isLoading ? (
                              "Loading"
                            ) : (
                              <ReactApexChart
                                key={JSON.stringify(orderSeries)}
                                series={orderSeries}
                                options={options}
                                type="line"
                                height={320}
                                className="apex-charts"
                              />
                            )}
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <CardTitle className="d-flex align-items-center gap-1">
                        {props.t("Gross Revenue")}{" "}
                        <i
                          className="bx bx-info-circle cursor-pointer"
                          id="grossRevenue"
                        ></i>
                        <UncontrolledTooltip
                          placement="top"
                          target={"grossRevenue"}
                        >
                          {props.t("Including discount and taxes")}
                        </UncontrolledTooltip>
                      </CardTitle>
                      {isLoading ? (
                        <ShimmerThumbnail height={320} rounded />
                      ) : (
                        <>
                          <h2>
                            {parseFloat(totalPayment).toFixed(2)}{" "}
                            {props.t("SAR")}
                          </h2>
                          <div style={{ direction: "ltr" }}>
                            <ReactApexChart
                              key={JSON.stringify(netPaymentSeries)}
                              series={netPaymentSeries}
                              options={options}
                              type="line"
                              height={320}
                              className="apex-charts"
                            />
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <CardTitle className="d-flex align-items-center gap-1">
                        {props.t("Net Revenue")}{" "}
                        <i
                          className="bx bx-info-circle cursor-pointer"
                          id="netRevenue"
                        ></i>
                      </CardTitle>
                      <UncontrolledTooltip
                        placement="top"
                        target={"netRevenue"}
                      >
                        {props.t("Excluding discount and taxes")}
                      </UncontrolledTooltip>
                      {isLoading ? (
                        <ShimmerThumbnail height={320} rounded />
                      ) : (
                        <>
                          <h2>
                            {parseFloat(totalSales)?.toFixed(2)}{" "}
                            {props.t("SAR")}
                          </h2>
                          <div style={{ direction: "ltr" }}>
                            <ReactApexChart
                              key={JSON.stringify(netSalesSeries)}
                              series={netSalesSeries}
                              options={options}
                              type="line"
                              height={320}
                              className="apex-charts"
                            />
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Total Discount")}</CardTitle>
                      {isLoading ? (
                        <ShimmerThumbnail height={320} rounded />
                      ) : (
                        <>
                          <h2>
                            {parseFloat(totalDiscount)?.toFixed(2)}{" "}
                            {props.t("SAR")}
                          </h2>
                          <div style={{ direction: "ltr" }}>
                            <ReactApexChart
                              key={JSON.stringify(discountSeries)}
                              series={discountSeries}
                              options={options}
                              type="line"
                              height={320}
                              className="apex-charts"
                            />
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Order Type")}</CardTitle>
                      {isLoading ? (
                        <ShimmerThumbnail height={320} rounded />
                      ) : (
                        <div style={{ direction: "ltr" }}>
                          <ReactApexChart
                            key={JSON.stringify(orderTypeSeries)}
                            series={orderTypeSeries}
                            options={options}
                            type="line"
                            height={320}
                            className="apex-charts"
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Hourly Sales")}</CardTitle>
                      {isLoading ? (
                        <ShimmerThumbnail height={320} rounded />
                      ) : (
                        <Bar
                          width={550}
                          height={180}
                          data={hourlySalesData}
                          options={barOptions}
                        />
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={4}>
                  <Card style={{ minHeight: 260 }}>
                    <CardBody>
                      <CardTitle>
                        {props.t("Top Revenue-Generating Branches")}
                      </CardTitle>
                      {!isLoading && (
                        <p className="fw-bold">
                          {dayType === "day" && date}
                          {(dayType === "week" || dayType === "month") &&
                            `${dateRange.start}  ${props.t("to")}  ${
                              dateRange.end
                            }`}
                        </p>
                      )}
                      {isLoading ? (
                        <ShimmerThumbnail height={400} rounded />
                      ) : (
                        <Pie
                          height={260}
                          data={orderBranchData}
                          options={{
                            plugins: {
                              legend: {
                                display: true,
                                position: "right",
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (tooltipItem) {
                                    // Access the dataset and data
                                    const dataset = tooltipItem?.dataset
                                    const value =
                                      dataset?.data[tooltipItem?.dataIndex] // Get the corresponding value
                                    const count =
                                      dataset?.counts[tooltipItem?.dataIndex] // Access the count from the custom property

                                    // Return the custom tooltip
                                    return [
                                      `${props.t(
                                        "Total Amount"
                                      )} : ${value} SAR`,
                                      `${props.t("Total Orders")}: ${count}`,
                                    ]
                                  },
                                },
                              },
                            },
                          }}
                        />
                      )}
                      {isCompared && (
                        <>
                          {!isLoading && (
                            <p className="fw-bold">
                              {dayType === "day" && comparisonDate}
                              {(dayType === "week" || dayType === "month") &&
                                `${comparisonDateRange.start} - ${comparisonDateRange.end}`}
                            </p>
                          )}
                          {isLoading ? (
                            <ShimmerThumbnail height={400} rounded />
                          ) : (
                            <Pie
                              height={260}
                              data={comparisonOrderBranchData}
                              options={{
                                plugins: {
                                  legend: {
                                    display: true,
                                    position: "right",
                                  },
                                },
                              }}
                            />
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col lg={4}>
                  <Card style={{ minHeight: 260 }}>
                    <CardBody>
                      <CardTitle>{props.t("Top Ordered Item")}</CardTitle>
                      {!isLoading && (
                        <p className="fw-bold">
                          {dayType === "day" && date}
                          {(dayType === "week" || dayType === "month") &&
                            `${dateRange.start} ${props.t("to")} ${
                              dateRange.end
                            }`}
                        </p>
                      )}
                      {isLoading ? (
                        <ShimmerThumbnail height={400} rounded />
                      ) : (
                        <Pie
                          height={260}
                          data={mostOrderedItemsData}
                          options={{
                            plugins: {
                              legend: {
                                display: true,
                                position: "right",
                              },
                            },
                          }}
                        />
                      )}
                      {isCompared && (
                        <>
                          {!isLoading && (
                            <p className="fw-bold">
                              {dayType === "day" && comparisonDate}
                              {(dayType === "week" || dayType === "month") &&
                                `${comparisonDateRange.start} - ${comparisonDateRange.end}`}
                            </p>
                          )}
                          {isLoading ? (
                            <ShimmerThumbnail height={400} rounded />
                          ) : (
                            <Pie
                              height={260}
                              data={comparisonMostOrderedItemsData}
                              options={{
                                plugins: {
                                  legend: {
                                    display: true,
                                    position: "right",
                                  },
                                },
                              }}
                            />
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card style={{ minHeight: 260 }}>
                    <CardBody>
                      <CardTitle>{props.t("Top Payment Method")}</CardTitle>
                      {!isLoading && (
                        <p className="fw-bold">
                          {dayType === "day" && date}
                          {(dayType === "week" || dayType === "month") &&
                            `${dateRange.start} ${props.t("to")} ${
                              dateRange.end
                            }`}
                        </p>
                      )}
                      {isLoading ? (
                        <ShimmerThumbnail height={400} rounded />
                      ) : (
                        <Pie
                          height={260}
                          data={mostPaymentMethodData}
                          options={{
                            plugins: {
                              legend: {
                                display: true,
                                position: "right",
                              },
                            },
                          }}
                        />
                      )}
                      {isCompared && (
                        <>
                          {!isLoading && (
                            <p className="fw-bold">
                              {dayType === "day" && comparisonDate}
                              {(dayType === "week" || dayType === "month") &&
                                `${comparisonDateRange.start} ${props.t(
                                  "to"
                                )} ${comparisonDateRange.end}`}
                            </p>
                          )}
                          {isLoading ? (
                            <ShimmerThumbnail height={400} rounded />
                          ) : (
                            <Pie
                              height={260}
                              data={comparisonMostPaymentMethodData}
                              options={{
                                plugins: {
                                  legend: {
                                    display: true,
                                    position: "right",
                                  },
                                },
                              }}
                            />
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {tabs === "branches" && (
            <>
              <Row className="dashboard_count_row">
                <Col>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Active Orders")}</CardTitle>
                      {loading ? (
                        <ShimmerThumbnail
                          height={30}
                          rounded
                          className="mb-0 pb-0"
                        />
                      ) : (
                        <h3>
                          {dashboardOrderData?.cardData?.activeOrdersCount ?? 0}
                        </h3>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Completed Orders")}</CardTitle>
                      {loading ? (
                        <ShimmerThumbnail
                          height={30}
                          rounded
                          className="mb-0"
                        />
                      ) : (
                        <h3>
                          {dashboardOrderData?.cardData?.completedOrdersCount ??
                            0}
                        </h3>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Cancelled Orders")}</CardTitle>
                      {loading ? (
                        <ShimmerThumbnail
                          height={30}
                          rounded
                          className="mb-0"
                        />
                      ) : (
                        <h3>
                          {dashboardOrderData?.cardData?.canceledOrdersCount ??
                            0}
                        </h3>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Active Orders Sales")}</CardTitle>
                      {loading ? (
                        <ShimmerThumbnail
                          height={30}
                          rounded
                          className="mb-0"
                        />
                      ) : (
                        <h3>
                          {dashboardOrderData?.cardData?.activeOrdersAmount ??
                            0}{" "}
                          {props.t("SAR")}
                        </h3>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col>
                  <Card>
                    <CardBody>
                      <CardTitle>{props.t("Completed Orders Sales")}</CardTitle>
                      {loading ? (
                        <ShimmerThumbnail
                          height={30}
                          rounded
                          className="mb-0"
                        />
                      ) : (
                        <h3>
                          {dashboardOrderData?.cardData
                            ?.completedOrdersAmount ?? 0}{" "}
                          {props.t("SAR")}
                        </h3>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Card>
                  <CardBody>
                    <CardTitle>{props.t("Branches Statistics")}</CardTitle>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: 600 }}
                    >
                      {loading ? (
                        <TableShimmer row={6} col={7} />
                      ) : dashboardOrderData?.tableData?.length === 0 ? (
                        <p className="text-center my-2">
                          {props.t("No branches data found")}
                        </p>
                      ) : (
                        <Table className="table mb-0 " hover>
                          <thead className="table-light fixed_thead full_th">
                            <tr>
                              <th>{props.t("S. No.")}</th>
                              <th>{props.t("Name")}</th>
                              <th>{props.t("Active Orders Count")}</th>
                              <th>{props.t("Completed Orders Count")}</th>
                              <th>{props.t("Canceled Orders Count")}</th>
                              <th>{props.t("Active Orders Amount")}</th>
                              <th>{props.t("Completed Orders Amount")}</th>
                            </tr>
                          </thead>
                          <tbody style={{ maxHeight: 300 }}>
                            {dashboardOrderData?.tableData?.map(
                              (item, index) => (
                                <tr
                                  key={item._id}
                                  onClick={() => {
                                    navigate(
                                      `/branches/branch-details?id=${item._id}`
                                    )
                                  }}
                                  className="cursor-pointer"
                                >
                                  <td>{index + 1}</td>
                                  <td>{item.name}</td>
                                  <td>{item.activeOrdersCount}</td>
                                  <td>{item.completedOrdersCount}</td>
                                  <td>{item.canceledOrdersCount}</td>
                                  <td>
                                    {item.activeOrdersAmount} {props.t("SAR")}
                                  </td>
                                  <td>
                                    {item.completedOrdersAmount}{" "}
                                    {props.t("SAR")}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Row>
            </>
          )}

          {tabs === "bookings" && (
            <Bookings
              options={options}
              mapSeriesData={mapSeriesData}
              convertTimesArrayToLocale={convertTimesArrayToLocale}
              bookingModal={bookingModal}
              setBookingModal={setBookingModal}
              formatDate={formatDate}
              bookingType={bookingType}
              setBookingType={setBookingType}
              functionRef={childFunctionRef}
              commonDateRange={commonDateRange}
              t={props.t}
              language={language}
            />
          )}

          {tabs === "ratings" && (
            <BranchRatings
              ratingModal={ratingModal}
              setRatingModal={setRatingModal}
              ratingBranches={ratingBranches}
              setRatingBranches={setRatingBranches}
              branchOptions={branchOptions}
              branchListOptions={branchListOptions}
              setIsAllSelected={setIsAllSelected}
              functionRef={ratingFunRef}
              commonDayType={commonDayType}
              commonDateRange={commonDateRange}
              timesUTC={timesUTC}
              convertTimeToUTCDate={convertTimeToUTCDate}
              t={props.t}
              language={language}
            />
          )}

          {tabs === "wallet" && <Wallet commonDateRange={commonDateRange} />}
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>{props.t("Filter")}</ModalHeader>
        <ModalBody>
          <div>
            <Label>{props.t("Select Branches")}</Label>
            <Select
              value={selectedBranches}
              isMulti={true}
              onChange={item => {
                if (item?.find(option => option.value === "*")) {
                  setSelectedBranches(branchListOptions)
                  setIsAllSelected(true)
                } else {
                  setSelectedBranches(item)
                  setIsAllSelected(item.length === branchListOptions.length)
                }
              }}
              options={branchOptions}
              placeholder={props.t("Select Branches")}
              closeMenuOnSelect={false}
            />
          </div>
          <div className="mt-2">
            <Label>{props.t("Select Country")}</Label>
            <Select
              value={selectedCountry}
              onChange={item => {
                setSelectedCountry(item)
                fetchRegionList(item.value)
                setSelectedRegion([])
                setSelectedCity([])
              }}
              options={countryList?.map(item => ({
                label: language === "sa" ? item.nameAr : item.name,
                value: item._id,
              }))}
              className="select2-selection"
              placeholder={props.t("Select Country")}
            />
          </div>
          {Object.keys(selectedCountry)?.length > 0 && (
            <div className="mt-2">
              <Label>{props.t("Select Region")}</Label>
              <Select
                value={selectedRegion}
                onChange={item => {
                  setSelectedRegion(item)
                  fetchCityList(selectedCountry.value, item.value)
                  setSelectedCity([])
                }}
                options={regionList?.map(item => ({
                  label: language === "sa" ? item.nameAr : item.name,
                  value: item._id,
                }))}
                className="select2-selection"
              />
            </div>
          )}
          {Object.keys(selectedRegion)?.length > 0 && (
            <div className="mt-2">
              <Label>{props.t("Select City")}</Label>
              <Select
                value={selectedCity}
                onChange={item => {
                  setSelectedCity(item)
                }}
                options={cityList?.map(item => ({
                  label: language === "sa" ? item.nameAr : item.name,
                  value: item._id,
                }))}
                // placeholder={"Select City"}
                className="select2-selection"
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            {props.t("Cancel")}
          </Button>
          <Button color="primary" onClick={handleBranchFilter}>
            {props.t("Save")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

Dashboard.propTypes = {
  t: PropTypes.any,
  chartsData: PropTypes.any,
  onGetChartsData: PropTypes.func,
}

export default withTranslation()(Dashboard)
